<template>
  <transition name="modal-transition">
    <div class="file-drop" v-if="dragover">
      <div class="file-drop__info">
        <UploadIcon16 />
        {{ info }}
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import UploadIcon16 from '@components/Icons/UploadIcon16.vue';

export default defineComponent({
  name: 'FileDrop',
  emits: { drop: null },

  components: {
    UploadIcon16,
  },

  props: {
    info: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      dragover: false,
    };
  },

  methods: {
    /**
     * Dragover event handler.
     *
     */
    handleDragover(event: DragEvent) {
      // Tells the browser which text hint it should show to the user
      event.dataTransfer.dropEffect = 'copy';

      if (event.dataTransfer.types.includes('Files')) {
        // Don't show drop target if elements are dragged from inside the page
        event.preventDefault();

        if (!this.dragover) this.dragover = true;
      }
    },

    /**
     * Dragleave event handler.
     */
    handleDragleave(event: DragEvent) {
      event.preventDefault();
      if (event.target === this.$el) this.dragover = false;
    },

    /**
     * Drop event handler.
     */
    handleDrop(event: DragEvent) {
      event.preventDefault();
      this.dragover = false;

      if (event.dataTransfer.files.length) {
        this.$emit('drop', event.dataTransfer.files);
      }
    },

    /**
     * Add all event listeners.
     */
    addEventListeners() {
      window.addEventListener('dragover', this.handleDragover);
      window.addEventListener('dragleave', this.handleDragleave);
      window.addEventListener('drop', this.handleDrop);
    },

    /**
     * Remove all event listeners.
     */
    removeEventListeners() {
      window.removeEventListener('dragover', this.handleDragover);
      window.removeEventListener('dragleave', this.handleDragleave);
      window.removeEventListener('drop', this.handleDrop);
    },
  },

  /**
   * Mounted hook.
   */
  mounted() {
    this.addEventListeners();
  },

  /**
   * Before destroy hook.
   */
  beforeUnmount() {
    this.removeEventListeners();
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
.file-drop {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 99999;
  display: grid;
  align-content: center;
  justify-content: center;
}

.file-drop__info {
  pointer-events: none;
  background: rgba(0, 0, 0, 0.7);
  color: colors.$color-white;
  font-size: 24px;
  border-radius: 4px;
  padding: 32px 40px;
  display: grid;
  grid-column-gap: 16px;
  grid-auto-flow: column;
  align-items: center;
}
</style>
